import React, { useState, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";

/**
 * LoanProductsModal displays the list of loans and their information for comparison
 *
 * @callback onHideCallback
 * @callback setPpwCallback
 * @callback setLoanAIdCallBack
 *
 * @param {boolean} show Determines if the modal is loaded and shown
 * @param {onHideCallback} onHide Callback to hide the modal when closed
 * @param {Array} loanList List of Loans available for the current customer
 * @param {setPpwCallback} setPpw Callback to update current PPW value
 * @param {CustomerData} customerData Object containing details on customer data
 * @param {string} provider Current loan provider (e.g. GoodLeap, Sungage, etc.)
 * @param {setLoanAIdCallBack} setLoanAId Callback to update current loan based on id
 * @param {Object} curLoan Current Selected Loan
 * @param {number} adderTotal Additional system cost adders
 * @param {number} ppw Price per watt
 * @param {number} dealerFee Dealer fee multiplier
 * @param {Function} fetchSungagePayments Async function to fetch Sungage monthly payments
 * @param {boolean} isB Determines alternate mode for pricing
 * @returns {React.JSX.Element}
 */

function LoanProductsModal({
  show,
  onHide,
  mappedPrices,
  loanList,
  setPpw,
  customerData,
  provider,
  setLoanAId,
  curLoan,
  adderTotal,
  ppw,
  dealerFee,
  fetchSungagePayments,
  setDealerFee,
  isB,
}) {
  const [radioValue, setRadioValue] = useState();
  const [sungagePayments, setSungagePayments] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [selectedPPW, setSelectedPPW] = useState();

  const ppwTimesDealerFee = +(ppw * dealerFee).toFixed(2);
  const defaultPpw =
    curLoan?.loanName === provider && curLoan?.ppwRate != null
      ? +curLoan.ppwRate
      : ppwTimesDealerFee;

  useEffect(() => {
    setRadioValue(curLoan.loanId);
  }, [curLoan]);

  useEffect(() => {
    if (show) {
      const value =
        curLoan?.loanName === provider && curLoan?.ppwRate != null
          ? +curLoan.ppwRate
          : ppwTimesDealerFee;
      setSelectedPPW(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, provider]);

  const handleRadioChange = (value) => setRadioValue(value);

  const handleSave = () => {
    if (provider === "SunGage") {
      const loan = loanList.find((l) => +l.loanId === +radioValue);
      if (!loan) {
        onHide();
        return;
      }
      const loanDealerFee = +loan.dealerFee || 1;
      if (Number.isFinite(loanDealerFee)) {
        setDealerFee(loanDealerFee);
      }
      const effectivePpw = selectedPPW != null ? selectedPPW / loanDealerFee : ppw;
      if (effectivePpw != null && Number.isFinite(effectivePpw)) {
        setPpw(effectivePpw, true);
      }
      const ppwRate = selectedPPW != null ? +selectedPPW : ppw * loanDealerFee;
      if (ppwRate != null && Number.isFinite(ppwRate)) {
        loan.ppwRate = ppwRate;
      }
      setLoanAId(loan);
    } else {
      const loan = loanList.find((l) => +l.loanId === +radioValue);
      const loanDealerFee = loan ? +loan.dealerFee || 1 : +dealerFee || 1;
      if (selectedPPW != null && Number.isFinite(selectedPPW) && loanDealerFee > 0) {
        setPpw(selectedPPW / loanDealerFee, true);
      }
      setLoanAId(radioValue);
    }
    onHide();
  };

  const onClose = () => {
    setRadioValue(curLoan.loanId);
    onHide();
  };

  const formatLoanName = (loan) => {
    if (provider === "GoodLeap PPA") {
      return `${loan.loanTerm} YR ${(loan.ppaEscalator * 100).toFixed(2)}% Escalation`;
    }
    return `${loan.loanTerm} YR ${loan.loanInterest} % APR`;
  };

  const calculateLoanAmount = (loan) => {
    const systemSize = +customerData.systemSize || 0;
    const loanDealerFee = +loan.dealerFee || 1;
    const downPayment = +customerData.downPayment || 0;

    return (
      ((systemSize * (+ppw * dealerFee)) + (+adderTotal * dealerFee) - downPayment) /
      loanDealerFee
    );
  };

  const fetchAll = async () => {
    const payments = {};
    for (const loan of loanList.filter((l) => l.loanName === provider)) {
      const loanAmount = calculateLoanAmount(loan);
      try {
        const result = await fetchSungagePayments(
          loan?.loanTerm,
          loan?.loanInterest,
          loanAmount,
          loan?.itc,
          loan?.ppaEscalator
        );

        loan.firstMonthPayment = result?.initialMonthlyPayment;
        loan.continuingMonthlyPayment = result?.continuingMonthlyPayment;
        loan.prePayment = result?.initialMonthlyPayment / loanAmount;
        loan.postPayment = result?.continuingMonthlyPayment / loanAmount;
        loan.loanInterest = loan?.loanInterest;
        loan.itc = loan?.itc;

        payments[loan.loanId] = result?.initialMonthlyPayment;
      } catch (err) {
        console.error("Error fetching Sungage payment", err);
        payments[loan.loanId] = null;
      }
    }
    setSungagePayments(payments);
    return payments;
  };

  useEffect(() => {
    let isMounted = true;
    if (provider === "SunGage") {
      setLoading(true);
      fetchAll().then(() => {
        if (isMounted) setLoading(false);
      });
    } else {
      setLoading(false);
    }
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, ppw]);

  const setChecked = (loan) => {
    return (+radioValue === +loan.loanId || 
      (curLoan.loanTerm === loan.loanTerm && 
        curLoan.loanInterest === loan.loanInterest && 
        curLoan.ppw === selectedPPW)); 
  }

  const createLoanName = (loan) => {
    // if (+loan.itc === 0 && !loan.loanName.includes("(No ITC)"))
    //   return loan.loanName + " (No ITC)";
    // else
      return loan.loanName;
  }

  const mappedLoans = loanList
    .filter((loan) => loan.loanName !== "GoodLeap Lease" 
      && loan.loanName !== "GoodLeap PPA" 
      && loan.loanName.includes(provider))
    .map((loan, i) => {
      const loanAmount = calculateLoanAmount(loan).toFixed(2);
      // If provider = SunGage, pull pre-fetched value and ensure it's numeric
      let monthlyPayment;
      if (provider === "SunGage") {
        const raw = sungagePayments[loan.loanId];
        const num = Number(raw);
        monthlyPayment = isNaN(num) ? null : num.toFixed(2);
      } else {
        monthlyPayment = (loan.prePayment * loanAmount).toFixed(2);
      }

      return (
        <tr key={i}>
          <td>
            <input
              type="radio"
              id={loan.loanId}
              name="loanOpts"
              value={i}
              checked={setChecked(loan)}
              onChange={(e) => handleRadioChange(e.target.id)}
            />
            <label htmlFor={loan.loanId}></label>
          </td>
          <td className="qualyText" scope="row">
            {formatLoanName(loan)}
          </td>
          <td className="qualyText" scope="row">
            {createLoanName(loan)}
          </td>
          {provider === "SunGage" && (
            <td className="qualyText" scope="row">
              {loan.ppaEscalator != null
                ? `${(loan.ppaEscalator * 100).toFixed(2)}%`
                : "—"}
            </td>
          )}
          <td className="qualyText" scope="row">
            {provider === "SunGage" && isLoading ? "Loading..." : `$${monthlyPayment}`}
          </td>
          <td className="qualyText" scope="row">
            $
            {loanAmount?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          </td>
        </tr>
      );
    });

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{provider} Financing - Loan Products</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="savingsText modals fin">Pricing</div>
        <select
          className="finInput prices"
          value={selectedPPW ?? defaultPpw}
          onChange={(e) => {
            const value = +e.target.value;
            setSelectedPPW(value);
            if (!isB) {
              setPpw(value, false);
              setRadioValue(undefined);
            }
          }}
        >
          {mappedPrices}
        </select>
        <div className="row">
          <table className="table table-striped table-borderless">
            <thead>
              <tr className="table-borderless">
                <th className="qualyText" scope="col"></th>
                <th className="qualyText" scope="col">
                  Name
                </th>
                <th className="qualyText" scope="col">
                  Provider
                </th>
                {provider === "SunGage" && (
                  <th className="qualyText" scope="col">
                    Escalator
                  </th>
                )}
                <th className="qualyText" scope="col">
                  Monthly Payment
                </th>
                <th className="qualyText" scope="col">
                  Total Cost
                </th>
              </tr>
            </thead>
            <tbody>{mappedLoans}</tbody>
          </table>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default LoanProductsModal;
